import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, CheckCircle2, XCircle, Wand2, Loader2, ChevronRight, AlertTriangle } from "lucide-react";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function RotaAiOptimiser({ open, onOpenChange, weekStart, weekEnd, onApplied }) {
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);       // {proposals, skipped, narrative, filled, unfilled}
  const [selected, setSelected] = useState({});     // {visit_id: bool}

  const runOptimise = async () => {
    setRunning(true); setResult(null); setSelected({});
    try {
      const r = await api.post("/rota/ai-optimize", { start_date: weekStart, end_date: weekEnd });
      const data = r.data || {};
      setResult(data);
      // pre-select all proposals by default
      const s = {};
      (data.proposals || []).forEach((p) => { s[p.visit_id] = true; });
      setSelected(s);
      if ((data.proposals || []).length === 0) {
        toast.info("No proposals — every shift is already assigned or no candidate carers available.");
      } else {
        toast.success(`AI proposed ${data.filled} assignment${data.filled === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setRunning(false); }
  };

  const toggle = (vid) => setSelected((prev) => ({ ...prev, [vid]: !prev[vid] }));
  const allChecked = result?.proposals?.length > 0 && result.proposals.every((p) => selected[p.visit_id]);
  const toggleAll = () => {
    const next = {};
    result.proposals.forEach((p) => { next[p.visit_id] = !allChecked; });
    setSelected(next);
  };

  const applyProposals = async () => {
    if (!result) return;
    const chosen = result.proposals.filter((p) => selected[p.visit_id]);
    if (chosen.length === 0) { toast.error("Nothing selected to apply."); return; }
    if (!window.confirm(`Apply ${chosen.length} AI proposal(s)? They will appear as DRAFT until you publish the rota.`)) return;
    setApplying(true);
    try {
      const r = await api.post("/rota/ai-apply", {
        proposals: chosen.map((p) => ({ visit_id: p.visit_id, staff_user_id: p.proposed_staff_id })),
      });
      toast.success(`Applied ${r.data.applied} assignment(s). Publish the rota to notify staff.`);
      onApplied?.();
      onOpenChange(false);
      setResult(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setApplying(false); }
  };

  const selectedCount = result ? Object.values(selected).filter(Boolean).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ai-optimiser-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" /> AI Rota Optimiser
          </DialogTitle>
          <DialogDescription>
            Auto-assigns unfilled shifts respecting staff leave, weekly hours (48h cap), overlaps, and
            client continuity. Review, tick which suggestions to keep, then apply.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10" data-testid="ai-optimiser-empty">
            <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-violet-700" />
            </div>
            <div className="text-sm text-slate-600 text-center max-w-md">
              Analyse this week&apos;s vacant shifts and propose the best-fit carer for each — based on
              availability, hours headroom, care continuity, and skills.
            </div>
            <Button
              onClick={runOptimise}
              disabled={running}
              className="bg-violet-700 hover:bg-violet-800 text-white"
              data-testid="btn-run-ai-optimise"
            >
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Optimise this week</>}
            </Button>
            <div className="text-[11px] text-slate-500">Range: {weekStart} → {weekEnd}</div>
          </div>
        )}

        {result && (
          <div className="flex-1 overflow-auto space-y-3" data-testid="ai-optimiser-result">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-2">
              <SummaryTile label="Proposed" value={result.filled} tone="ok" testid="ai-summary-filled" />
              <SummaryTile label="Selected" value={selectedCount} tone="info" testid="ai-summary-selected" />
              <SummaryTile label="Unfilled" value={result.unfilled} tone={result.unfilled > 0 ? "warn" : "ok"} testid="ai-summary-unfilled" />
            </div>

            {result.narrative && (
              <div className="bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 rounded-md p-3 text-[12px] text-violet-900 dark:text-violet-200" data-testid="ai-narrative">
                <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold mb-0.5">Summary</div>
                {result.narrative}
              </div>
            )}

            {result.proposals?.length > 0 && (
              <div className="border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden">
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 px-3 py-2 text-xs font-semibold">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="ai-toggle-all" />
                    Toggle all ({result.proposals.length})
                  </label>
                  <div className="text-[11px] text-slate-500">Confidence · Reasoning</div>
                </div>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-auto">
                  {result.proposals.map((p) => (
                    <li key={p.visit_id} className="px-3 py-2 flex items-start gap-2 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`ai-proposal-${p.visit_id}`}>
                      <input
                        type="checkbox"
                        checked={!!selected[p.visit_id]}
                        onChange={() => toggle(p.visit_id)}
                        className="mt-1"
                        data-testid={`ai-check-${p.visit_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-slate-900 dark:text-slate-100 truncate">{p.client_name || "Service user"}</span>
                          <ChevronRight className="w-3 h-3 text-slate-400" />
                          <span className="font-semibold text-violet-700 dark:text-violet-300 truncate">{p.proposed_staff_name}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                          <span>{fmtWhen(p.scheduled_start)}</span>
                          <span>·</span>
                          <span>Weekly hrs after: <span className="font-semibold tabular-nums">{p.projected_weekly_hours}h</span></span>
                        </div>
                        <div className="text-[11px] text-slate-600 dark:text-slate-300 italic mt-0.5">{p.reasoning}</div>
                      </div>
                      <div className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                        p.confidence >= 90 ? "bg-emerald-100 text-emerald-800" :
                        p.confidence >= 70 ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"
                      }`}>{p.confidence}%</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.skipped?.length > 0 && (
              <div className="border border-amber-200 dark:border-amber-800 rounded-md" data-testid="ai-skipped">
                <div className="bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-[11px] font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> Unfilled ({result.skipped.length}) — needs manual attention
                </div>
                <ul className="text-[11px] px-3 py-1.5 space-y-0.5 text-amber-900 dark:text-amber-200">
                  {result.skipped.slice(0, 8).map((s, i) => (
                    <li key={i}>· <span className="font-medium">{s.client_name}</span> — {fmtWhen(s.scheduled_start)} · {s.reason}</li>
                  ))}
                  {result.skipped.length > 8 && <li className="italic">…and {result.skipped.length - 8} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result && (
            <Button variant="outline" onClick={runOptimise} disabled={running} data-testid="btn-ai-rerun">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              Re-run
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {result && (
            <Button
              onClick={applyProposals}
              disabled={applying || selectedCount === 0}
              className="bg-violet-700 hover:bg-violet-800 text-white"
              data-testid="btn-ai-apply"
            >
              {applying ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Applying…</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Apply {selectedCount} suggestion{selectedCount === 1 ? "" : "s"}</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryTile({ label, value, tone, testid }) {
  const tones = {
    ok: "bg-emerald-50 border-emerald-200 text-emerald-900",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
    info: "bg-violet-50 border-violet-200 text-violet-900",
  };
  return (
    <div className={`border rounded-md p-2 text-center ${tones[tone]}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest font-semibold opacity-75">{label}</div>
      <div className="text-xl font-heading font-semibold tabular-nums leading-tight">{value}</div>
    </div>
  );
}
